// クラシック版の保存データとストレージ（段階 C の2 の1。2026-10-25 に charat.common.js・chibi.js から描き直した。動きは前と同じ）。
// 保存データの文字（レイヤーの属性から）・localStorage と sessionStorage・zlib の圧縮と展開・キャラのデータの読み込み・作りかけの保存と読み込み・
// IndexedDB のキャッシュ・同意の印。前のエンジンの名前（charat.common.* と chibi.*）に置く（main.ts）
import { chibi, common, indexedDBOf, zlib } from './engine.js';

// ---- 保存データの文字 ----

/**
 * キャラ復元文字列取得。各レイヤーの param 属性から保存データ {v: 版, n: 名前, c: [...]} の JSON を作る。anOrgStr が true なら JSON のまま、false なら zlib で圧縮した文字列を返す。
 * aRoot: レイヤー g（g[id^="lcart_"]）の並び（jQuery の集合でもよい）、aName: [名前（URL エンコード済み）, 名前の色]、aLayerKey: レイヤーID の接頭辞 "lcart_"、aVer: 保存形式の版（chibi.ATTR_SAVEVER）。
 * c の1件: "カテゴリ,パーツ,色,色From,色To,反転,X,Y,拡大,左From,左To,右From,右To,柄,柄プレビュー,柄モード,回転,不透明度,"（末尾にもカンマが付く）
 */
export function getSaveText(aRoot: ArrayLike<Element>, aName: unknown, aLayerKey: string, aVer: unknown, anOrgStr: boolean): string {
  const csel = { v: aVer, n: aName, c: [] as string[] };
  const fields = chibi().SAVE_FIELDS;
  Array.prototype.forEach.call(aRoot, (layerNode: Element) => {
    // パーツ未選択（paramParts が ""）のレイヤーは書かない（属性が無いレイヤーは書く。前と同じ）
    if (layerNode.getAttribute('paramParts') === '') return;
    // レイヤーID から接頭辞と _f / _r / _y を取ってカテゴリ名にする（g なので名前の途中の "_f" なども全部消える。前と同じ）
    const categoryName = layerNode.id.replace(new RegExp(aLayerKey, 'g'), '').replace(/_f/g, '').replace(/_r/g, '').replace(/_y/g, '');
    const attr = (name: string) => layerNode.getAttribute(name) + ',';
    // 並びが違うメーカー（chibi.SAVE_FIELDS。origin など）はその並びで書く
    if (fields) {
      csel.c.push(categoryName + ',' + fields.map(attr).join(''));
      return;
    }
    const names = ['paramParts', 'paramCol', 'paramColFrom', 'paramColTo', 'paramRev', 'paramTx', 'paramTy', 'paramScale', 'paramColLFrom', 'paramColLTo',
      'paramColRFrom', 'paramColRTo', 'paramPtn', 'paramPrev', 'paramPtnMode', 'paramRotate', 'paramOpacity'];
    csel.c.push(categoryName + ',' + names.map(attr).join(''));
  });
  const orgstr = JSON.stringify(csel);
  return anOrgStr ? orgstr : strToZipStr(orgstr);
}

// ---- localStorage・sessionStorage ----

export function isUseLocalStorage(): boolean {
  try {
    return typeof localStorage !== 'undefined' && !!window.localStorage;
  } catch (ex) {
    return false;
  }
}
export function isUseSessionStorage(): boolean {
  try {
    return typeof sessionStorage !== 'undefined' && !!window.sessionStorage;
  } catch (ex) {
    return false;
  }
}
/**
 * 指定キーの値を保存する（aLocal が false なら sessionStorage）。保存できたら true。
 * 前のエンジンは使えるかの確かめで関数を呼ばずに見ていた（いつも true）ので、ここでも確かめずに書いて、例外なら false
 */
export function setStrage(aKey: string, aValue: string, aLocal?: boolean): boolean {
  try {
    if (aLocal == null || aLocal) localStorage.setItem(aKey, aValue);
    else sessionStorage.setItem(aKey, aValue);
    return true;
  } catch (ex) {
    return false;
  }
}
/** 指定キーの値を取得する（aLocal が false なら sessionStorage）。無ければ・使えなければ null */
export function getStrage(aKey: string, aLocal?: boolean): string | null {
  try {
    if (aLocal == null || aLocal) return isUseLocalStorage() ? localStorage.getItem(aKey) : null;
    return isUseSessionStorage() ? sessionStorage.getItem(aKey) : null;
  } catch (ex) {
    return null;
  }
}

// ---- zlib（保存データの圧縮。1文字を1バイトとして扱う） ----

/** 文字列をバイト配列に（1文字を1バイト。255 を超える文字は下位8ビットだけ。保存データの名前は URL エンコード済みなので問題にならない） */
export function strToByteArray(aStr: string): Uint8Array {
  // 1 つずつ UTF-16 の単位で（文字列の for…of は2単位の文字を1つにまとめるので使わない。前の [].map.call と同じ）
  const out = new Uint8Array(aStr.length);
  for (let i = 0; i < aStr.length; i++) out[i] = aStr.charCodeAt(i);
  return out;
}
/** バイト配列を文字列に */
export function byteArrayToStr(aBuf: ArrayLike<number> | ArrayBufferLike): string {
  return String.fromCharCode.apply('', new Uint8Array(aBuf as ArrayLike<number>) as unknown as number[]);
}
/** 圧縮した状態の文字列を通常の文字列に */
export function zipStrToStr(str: string): string {
  return byteArrayToStr(new (zlib().Inflate)(strToByteArray(str)).decompress());
}
/** 文字列を圧縮した状態の文字列に */
export function strToZipStr(str: string): string {
  return byteArrayToStr(new (zlib().Deflate)(strToByteArray(str)).compress());
}

/** 保存ファイル名（名前_年月日_時分秒.拡張子） */
export function getSaveFileName(svname: string, ext: string): string {
  const now = new Date();
  const two = (n: number) => ('0000' + n).substr(-2);
  return svname + '_' + now.getFullYear() + two(now.getMonth() + 1) + two(now.getDate()) + '_' + two(now.getHours()) + two(now.getMinutes()) + two(now.getSeconds()) + (ext != '' ? '.' + ext : '');
}

// ---- 通信 ----

/**
 * キャラのデータ（getcategory.php）を読む。読めたら中身の文字、読めなければ null。2xx か 304 なら読めた。
 * 送る形 "key = dataload&comp = ..." は空白入りのまま（getcategory.php も $_POST['key_'] == ' dataload'・$_POST['comp_'] == ' true' で受けている）
 */
export function fetchCategoryText(aUrl: string, aCompress: unknown): Promise<string | null> {
  return fetch(aUrl, {
    method: 'POST',
    headers: { 'X-Requested-With': 'XMLHttpRequest', 'Content-Type': 'application/x-www-form-urlencoded' },
    body: 'key = dataload&comp = ' + aCompress,
  })
    .then((res) => (res.ok || res.status === 304 ? res.text() : null))
    .catch(() => null);
}
/** フォームの形で送り、返事を JSON で読む。読めなければ null（配列は「名前[]」で1つずつ、X-Requested-With 付き。jQuery の $.post と同じ形） */
export function postJSON(aUrl: string, aParam: Record<string, unknown>): Promise<unknown> {
  const body = new URLSearchParams();
  Object.keys(aParam).forEach((key) => {
    const value = aParam[key];
    if (Array.isArray(value)) value.forEach((v) => body.append(key + '[]', String(v)));
    else body.append(key, String(value));
  });
  return fetch(aUrl, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body })
    .then((res) => (res.ok ? res.json() : null))
    .catch(() => null);
}

// ---- 作りかけの保存と読み込み（chibi） ----

/** 今のキャラの保存データ（圧縮した文字）を作り、aKey に保存する（保存できなくても文字は返す） */
export function saveChara(aKey: string): string {
  const c = chibi();
  const cstr = getSaveText(document.querySelectorAll(c.DIV_F_MAKE + " g[id^='" + c.ATTR_LAYER + "']"), c.btnMenuName.GET(true), c.ATTR_LAYER, c.ATTR_SAVEVER, false);
  setStrage(aKey, encodeURIComponent(cstr));
  return cstr;
}
/** aKey の保存データでキャラを組み立てる。組み立てたら true（aLoadingMes なら「作っています」を出す。読めない・壊れているときは false） */
export function loadChara(aKey: string, aLoadingMes?: boolean): boolean {
  const cstr = getStrage(aKey);
  if (cstr == null || cstr == '') return false;
  try {
    const c = chibi();
    if (aLoadingMes == true) {
      common().setNowMaking(true, 1, 0, 0, c.DIV_F_CONTENTS);
      common().setNowMaking(false, 1, 1200, 300, c.DIV_F_CONTENTS);
    }
    c.makeCharactor(JSON.parse(zipStrToStr(decodeURIComponent(cstr))), false, true);
    return true;
  } catch (e) {
    return false;
  }
}

// ---- IndexedDB のキャッシュ（キャラのデータ） ----

export function makeDB(e: IDBVersionChangeEvent): void {
  const db = (e.target as IDBOpenDBRequest).result;
  db.createObjectStore(chibi().CACHE_TBL_CHARA, { keyPath: chibi().CACHE_TBL_CHARA_KEY });
}
/** データ保存（キャッシュ）。終わったら aCallBack（失敗ならそのエラー、成功なら null） */
export function saveDB(aKey: string, aData: string, aCallBack?: (result: unknown) => void): void {
  const idb = indexedDBOf();
  if (!idb) {
    if (aCallBack) aCallBack(null);
    return;
  }
  const c = chibi();
  const conn = idb.open(c.CACHE_DB, c.CACHE_DB_VAR);
  conn.onupgradeneeded = makeDB;
  conn.onsuccess = (e) => {
    try {
      const db = (e.target as IDBOpenDBRequest).result;
      const trans = db.transaction(c.CACHE_TBL_CHARA, 'readwrite');
      const store = trans.objectStore(c.CACHE_TBL_CHARA);
      const putReq = store.put({ id: aKey, data: aData.replace(/<g i/i, '<XX') });
      let putresult: unknown = null;
      putReq.onsuccess = () => {};
      putReq.onerror = (ev) => {
        putresult = ev;
      };
      trans.oncomplete = () => {
        db.close();
        if (aCallBack) aCallBack(putresult);
      };
    } catch (ex) {
      if (aCallBack) aCallBack(ex);
    }
  };
  conn.onerror = (e) => {
    if (aCallBack) aCallBack(e);
  };
}
/** データ読み込み（キャッシュ）。aUse が false なら読まずに null。終わったら aCallBack（中身か null） */
export function loadDB(aUse: boolean, aKey: string, aCallBack?: (data: string | null) => void): void {
  const idb = indexedDBOf();
  if (!aUse || !idb) {
    if (aCallBack) aCallBack(null);
    return;
  }
  const c = chibi();
  const conn = idb.open(c.CACHE_DB, c.CACHE_DB_VAR);
  conn.onupgradeneeded = makeDB;
  conn.onsuccess = (e) => {
    try {
      const db = (e.target as IDBOpenDBRequest).result;
      const trans = db.transaction(c.CACHE_TBL_CHARA, 'readonly');
      const store = trans.objectStore(c.CACHE_TBL_CHARA);
      const getReq = store.get(aKey);
      let getresult: string | null = null;
      getReq.onsuccess = () => {
        // 注意: キャッシュが無いと result が undefined でここで例外になる（前のエンジンと同じ。aCallBack は null で呼ばれる）
        getresult = (getReq.result as { data: string }).data;
        getresult = getresult.replace(/<XX/i, '<g i');
      };
      getReq.onerror = () => {};
      trans.oncomplete = () => {
        db.close();
        if (aCallBack) aCallBack(getresult);
      };
    } catch (ex) {
      if (aCallBack) aCallBack(null);
    }
  };
  conn.onerror = () => {
    if (aCallBack) aCallBack(null);
  };
}

// ---- ユーザーID・リロード・同意の印 ----

/** ユーザーID をロードする（無ければ aNewUserID を保存して使う） */
export function loadUserID(aNewUserID: string): void {
  const c = chibi();
  let rval = getStrage(c.CACHE_USERID);
  if (rval == null) {
    rval = aNewUserID;
    setStrage(c.CACHE_USERID, rval);
  }
  c.UserID = rval;
}
/** リロード状況をロードする（前に開いてから aSec 秒以内ならキャッシュを使う） */
export function loadReload(aSec: number): void {
  const rval = getStrage(chibi().CACHE_RELOAD, false);
  if (rval != null) chibi().isUseCache = new Date().getTime() - Number(rval) < aSec * 1000;
}
/**
 * 初回確認（ガイドライン・利用規約への同意）がまだか。同意の印は CHARAT 全体で1つ（charat.agree.js の charatAgree、2026-10-20〜）:
 * ほかのメーカー（新しい作る画面も）で同意していれば出さない（charatAgree が無いページは前のとおりメーカーの印だけ見る）
 */
export function isFirst(): boolean {
  if (typeof charatAgree !== 'undefined') return !charatAgree.agreed();
  return getStrage(chibi().CACHE_GLAGREE) == null;
}
/** 初回確認済みの印を保存する（charatAgree があれば CHARAT 共通の印とこのメーカーの印を両方。aValue は前の形の印の値） */
export function setFirst(aValue: string): void {
  if (typeof charatAgree !== 'undefined') {
    charatAgree.set(chibi().CACHE_GLAGREE);
    return;
  }
  setStrage(chibi().CACHE_GLAGREE, aValue);
}
